import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import ROICalculator from "@/components/ROICalculator";

const EASE = [0.16, 1, 0.3, 1];

const lines = [
    { text: "DOMINATE YOUR", cls: "text-white" },
    { text: "PIPELINE WITH", cls: "text-white" },
    { text: "AI & AUTOMATION.", cls: "text-stroke-red" },
];

function ParticleCanvas() {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        let w, h, raf;
        const pts = [];
        const N = 60;

        const resize = () => {
            w = canvas.width = canvas.offsetWidth * devicePixelRatio;
            h = canvas.height = canvas.offsetHeight * devicePixelRatio;
        };
        resize();
        window.addEventListener("resize", resize);

        for (let i = 0; i < N; i++) {
            pts.push({
                x: Math.random(),
                y: Math.random(),
                vx: (Math.random() - 0.5) * 0.0006,
                vy: (Math.random() - 0.5) * 0.0006,
            });
        }

        const draw = () => {
            ctx.clearRect(0, 0, w, h);
            for (const p of pts) {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > 1) p.vx *= -1;
                if (p.y < 0 || p.y > 1) p.vy *= -1;
            }
            for (let i = 0; i < N; i++) {
                for (let j = i + 1; j < N; j++) {
                    const dx = (pts[i].x - pts[j].x) * w;
                    const dy = (pts[i].y - pts[j].y) * h;
                    const d = Math.hypot(dx, dy);
                    if (d < w * 0.09) {
                        ctx.strokeStyle = `rgba(255,59,48,${0.14 * (1 - d / (w * 0.09))})`;
                        ctx.lineWidth = devicePixelRatio;
                        ctx.beginPath();
                        ctx.moveTo(pts[i].x * w, pts[i].y * h);
                        ctx.lineTo(pts[j].x * w, pts[j].y * h);
                        ctx.stroke();
                    }
                }
                ctx.fillStyle = "rgba(248,250,252,0.5)";
                ctx.beginPath();
                ctx.arc(pts[i].x * w, pts[i].y * h, 1.4 * devicePixelRatio, 0, Math.PI * 2);
                ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 220]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, -90]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            ref={sectionRef}
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden px-4 sm:px-8 lg:px-16 pt-28 pb-20"
        >
            <motion.div style={{ y: yBg, opacity }} className="absolute inset-0">
                <ParticleCanvas />
                <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#FF3B30]/10 blur-[140px]" />
                <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-[#FF2A00]/8 blur-[120px]" />
            </motion.div>

            <div className="relative z-10 max-w-[1400px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-8">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
                        className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-semibold mb-8"
                    >
                        Sales Performance Engineering — Canada
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl"
                    >
                        {lines.map((l, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${l.cls}`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.1, delay: 0.55 + i * 0.14, ease: EASE }}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                        className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
                    >
                        SalesCan transforms sluggish sales cycles into automated revenue
                        engines. Analytics, automations and AI copilots — everything to make
                        your team's life easier and your revenue grow.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-book-consultation-button"
                            onClick={() => scrollToId("#contact")}
                            className="group flex items-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300 shadow-[0_0_32px_rgba(255,59,48,0.4)] hover:shadow-[0_0_56px_rgba(255,59,48,0.65)]"
                        >
                            Book a Free Consultation
                            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </button>
                        <button
                            data-testid="hero-explore-process-button"
                            onClick={() => scrollToId("#process")}
                            className="group flex items-center gap-2 border border-white/15 hover:border-[#FF3B30]/60 text-slate-300 hover:text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
                        >
                            Explore Our Process
                            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: yCard }}
                    className="lg:col-span-4"
                >
                    <motion.div
                        initial={{ opacity: 0, x: 60 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.1, delay: 1.2, ease: EASE }}
                    >
                        <ROICalculator />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
